import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import CallexLoader from '../../../components/CallexLoader';

import {
  areaOptionsGet,
  beatOptionsGetById,
  outletOptionsGetById,
  productReportGet
} from '../../../store/slices/tenantSlices/productSlice';
import { userOptionGet } from '../../../store/slices/tenantSlices/agentPerformanceSlice';
import ProductReportDetails from './ProductReportDetails';
import { checkModulePermission, formatPayloadDate } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const ProductReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();

  const userRolePermission = useUserRolePermission();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [areaOption, setAreaOption] = useState([]);
  const [beatOption, setBeatOption] = useState([]);
  const [outletOption, setOutletOption] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [infoDetails, setInfoDetails] = useState(null);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    user_id: '',
    area_id: '',
    beat_id: '',
    outlet_id: ''
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        user_id: values?.user_id || undefined,
        area_id: values?.area_id || undefined,
        beat_id: values?.beat_id || undefined,
        outlet_id: values?.outlet_id || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(productReportGet(payload)).unwrap();
      if (response) {
        setReportData(response || []);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getAreaOption = async () => {
    try {
      const response = await dispatch(areaOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAreaOption(options);
      } else {
        setAreaOption([]);
      }
    } catch (error) {
      setAreaOption([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(userOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getUserOption();
    getAreaOption();
  }, []);

  const getBeatOption = async () => {
    try {
      const response = await dispatch(beatOptionsGetById({ area_id: values.area_id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setBeatOption(options);
      } else {
        setBeatOption([]);
      }
    } catch (error) {
      setBeatOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values.area_id) {
      getBeatOption();
    } else {
      setBeatOption([]);
    }
  }, [values.area_id]);

  const getOutletOption = async () => {
    try {
      const response = await dispatch(outletOptionsGetById({ beat_id: values.beat_id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setOutletOption(options);
      } else {
        setOutletOption([]);
      }
    } catch (error) {
      setOutletOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values.beat_id) {
      getOutletOption();
    } else {
      setOutletOption([]);
    }
  }, [values.beat_id]);

  useEffect(() => {
    onSubmit();
  }, [values?.limit, values?.page]);

  return (
    <div>
      <AdminHeader
        title="Product Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  minDate={values.start_date}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.user_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="user_id"
                  options={userOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Area"
                  placeholder="Select Area"
                  value={values.area_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="area_id"
                  options={areaOption}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Beat"
                  placeholder="Select Beat"
                  value={values.beat_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="beat_id"
                  options={beatOption}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Outlet"
                  placeholder="Select Outlet"
                  value={values.outlet_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="outlet_id"
                  options={outletOption}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 450px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header_fields?.default?.map((val, index) => {
                    if (val?.name === 'total_quantity' || val?.name === 'total_amount') {
                      return (
                        <th key={index} className="text-center" style={{ width: '120px' }}>
                          {val.label}
                        </th>
                      );
                    }
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th style={{ width: '90px' }}>Information</th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header_fields?.default?.map((value, idx) => {
                        return (
                          <td
                            key={idx}
                            className={`${value?.name === 'total_quantity' || value?.name === 'total_amount' ? 'text-end' : ''}`}>
                            {val[value?.name] || '-'}
                          </td>
                        );
                      })}
                      <td>
                        <div
                          className={`cursor-pointer text-info text-center ${
                            !checkModulePermission(
                              'is_export',
                              'Product Report',
                              userRolePermission
                            )
                              ? 'callex-disabled'
                              : ''
                          }`}
                          onClick={() => {
                            if (
                              checkModulePermission(
                                'is_export',
                                'Product Report',
                                userRolePermission
                              )
                            ) {
                              setInfoDetails(val);
                            }
                          }}>
                          <Icon.Info size={16} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData?.total}
              limit={reportData?.limit}
              page={reportData?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <ProductReportDetails
        isOpen={!!infoDetails}
        onCloseClick={() => setInfoDetails(null)}
        infoDetails={infoDetails}
      />
    </div>
  );
};

export default ProductReport;
